import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_IMAGE,
  DEFAULT_TITLE,
  SITE_NAME,
  SITE_URL,
  absoluteUrl,
  toDescription,
} from "../../../utils/seo";

/**
 * Sayfa bazlı SEO etiketleri.
 * - title: sayfa başlığı (" | muzisyen.app" eklenir); verilmezse site varsayılanı kullanılır.
 * - description: HTML olabilir, temizlenip 160 karaktere kısaltılır.
 * - path: canonical yol; verilmezse mevcut yol (query string hariç) kullanılır.
 * - noindex: arama sonuçlarına girmemesi gereken sayfalar (form, hesap, bulunamadı vb.).
 * - jsonLd: schema.org yapılandırılmış veri (nesne veya dizi).
 */
const Seo = ({
  title,
  description,
  image,
  imageAlt,
  type = "website",
  path,
  noindex = false,
  jsonLd,
}) => {
  const location = useLocation();
  const canonical = SITE_URL + (path || location.pathname);
  const pageTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
  const pageDescription = toDescription(description) || DEFAULT_DESCRIPTION;
  const pageImage = absoluteUrl(image) || DEFAULT_IMAGE;
  const structuredData = [].concat(jsonLd || []).filter(Boolean);

  return (
    // defer=false: etiketler requestAnimationFrame beklemeden, render ile aynı anda yazılır (botlar için).
    <Helmet defer={false}>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta
        name="robots"
        content={
          noindex
            ? "noindex, follow"
            : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        }
      />
      <link rel="canonical" href={canonical} />

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="tr_TR" />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title || DEFAULT_TITLE} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:image:alt" content={imageAlt || title || SITE_NAME} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@muzisyenapp" />
      <meta name="twitter:title" content={title || DEFAULT_TITLE} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />

      {structuredData.map((data, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(data).replace(/</g, "\\u003c")}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
